import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  NumberInput,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconBuilding,
  IconClipboardCheck,
  IconCoin,
  IconDownload,
  IconFileText,
  IconInfoCircle,
  IconMapPin,
  IconScale,
  IconSignature,
  IconWheat,
} from '@tabler/icons-react';
import SectionLabel from '@/components/common/SectionLabel';
import DealSummaryCard from '@/components/common/DealSummaryCard';
import UploadZone from '@/components/app/company/UploadZone';
import LoadingConditionsFields from '@/components/app/sell/form/LoadingConditionsFields';
import SellerCoreTermsView from '@/components/app/sell/SellerCoreTermsView';
import FileList from '@/components/app/sell/form/FileList';
import FormErrorsSummary from '@/components/common/FormErrorsSummary';
import {
  buildLoadingConditions,
  DECLARATION_NO_MAX,
  initialSellForm,
  isDealLogisticsComplete,
  loadingDateErrors,
  WEIGHT_MIN,
  type SellFormState,
} from '@/components/app/sell/form/sellFormState';
import { orderService } from '@/api/services/OrderService';
import { sellService } from '@/api/services/SellService';
import { ApiError, getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import { decodeEntities, formatMoney, formatOrgName } from '@/lib/format';
import { isoToRu, todayIso } from '@/lib/date';
import { ROUTES } from '@/routes/paths';
import {
  percentKeyFromPrepayment,
  prepaymentTypeFromPercent,
  type SellDealParams,
  type SellPrepaymentType,
} from '@/types/sellDeal';
import IdText from '@/components/common/IdText';

const POLL_INTERVAL_MS = 800;
const POLL_DEADLINE_MS = 60_000;

class DealSuspendedError extends Error {}
class DealTimeoutError extends Error {}

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Встречная закупка покупателя под мой оффер на продажу: продавец смотрит условия покупателя, формирует
// и подписывает поручение агенту, затем создаёт сделку («Продать»). Зеркало OfferPreviewPage (покупка),
// но создание требует подписанного поручения (agentAssignmentFileId) — поэтому шаг генерация→подпись.
export default function PurchaseOfferPreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  const [params] = useSearchParams();

  const orderId = Number(params.get('purchaseOrderId'));
  const orderVersion = Number(params.get('purchaseOrderVersion'));
  const fromSaleOrderIdRaw = Number(params.get('fromSaleOrderId'));
  // Заявка продавца есть → логистику резолвит бэкенд (read-only). Нет → «свободный» режим из поиска:
  // продавец сам заполняет объём + условия погрузки.
  const fromSaleOrderId = Number.isFinite(fromSaleOrderIdRaw) && fromSaleOrderIdRaw > 0 ? fromSaleOrderIdRaw : undefined;
  const freeMode = fromSaleOrderId == null;
  // Выгода «к вашей цене» приходит из ссылки (на странице её неоткуда взять). Из поиска (без привязки) —
  // не показываем, только цену.
  const advParam = params.get('advantage');
  const advantage = !freeMode && advParam != null && advParam !== '' && Number.isFinite(Number(advParam)) ? Number(advParam) : undefined;
  // Моя (целевая) цена — авторитетное значение с бэкенда через ссылку (не реконструируем из лучшей и
  // выгоды). В свободном режиме отсутствует.
  const myPriceParam = params.get('myPrice');
  const myPrice = !freeMode && myPriceParam != null && myPriceParam !== '' && Number.isFinite(Number(myPriceParam)) ? Number(myPriceParam) : undefined;
  const fromAddress = params.get('fromAddress');
  const fromAddressTitle = params.get('fromAddressTitle');
  const goods = params.get('goods');
  const initialPrepayment: SellPrepaymentType = params.get('prepayment') === 'PERCENT_100' ? 'PERCENT_100' : 'PERCENT_0';

  const valid =
    Number.isFinite(orderId) &&
    orderId > 0 &&
    Number.isFinite(orderVersion) &&
    !!fromAddress &&
    !!fromAddressTitle;

  const [comment, setComment] = useState('');
  const [agreeSupply, setAgreeSupply] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  // Поручение агенту: id файла + подписано ли. Источник истины — здесь.
  const [assignmentFileId, setAssignmentFileId] = useState<number | null>(null);
  const [assignmentSigned, setAssignmentSigned] = useState(false);

  // Логистика продавца — только в свободном режиме (без заявки). Переиспользуем срез состояния оффер-формы.
  // Оборудование инициализируем из ссылки «Продать по ней» — чтобы цена сделки считалась по тем же
  // параметрам, что видел продавец в ориентире. Отсутствующие параметры оставляем дефолтными (всё включено).
  const [logistics, setLogistics] = useState<SellFormState>(() => {
    const base = initialSellForm();
    const parseBool = (v: string | null): boolean | undefined => (v === 'true' ? true : v === 'false' ? false : undefined);
    const hs = parseBool(params.get('hasScales'));
    const tw = parseBool(params.get('trawls'));
    const tr = parseBool(params.get('trailers'));
    const slRaw = params.get('scalesLength');
    const sl = slRaw != null && slRaw !== '' && Number.isFinite(Number(slRaw)) ? Number(slRaw) : undefined;
    return {
      ...base,
      hasScales: hs ?? base.hasScales,
      trawls: tw ?? base.trawls,
      trailers: tr ?? base.trailers,
      scalesLength: sl ?? base.scalesLength,
    };
  });
  const set = useCallback((patch: Partial<SellFormState>) => setLogistics((s) => ({ ...s, ...patch })), []);
  // Оборудование в цену сделки проводим только в свободном режиме; с заявкой сервер берёт его из заявки.
  const viewHasScales = freeMode ? logistics.hasScales : undefined;
  const viewHasTrawl = freeMode ? logistics.trawls : undefined;
  const viewHasTrailer = freeMode ? logistics.trailers : undefined;
  const shipmentModes = useQuery({
    queryKey: ['shipmentModes'] as const,
    queryFn: ({ signal }) => sellService.getShipmentModes(signal),
    enabled: isAuthenticated && freeMode,
    staleTime: 10 * 60_000,
  });

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const view = useQuery({
    queryKey: [
      'purchaseOrderView',
      orderId,
      orderVersion,
      fromSaleOrderId,
      fromAddress,
      viewHasScales,
      viewHasTrawl,
      viewHasTrailer,
    ] as const,
    queryFn: ({ signal }) =>
      orderService.getPurchaseOrderView(
        {
          orderId,
          orderVersion,
          fromSaleOrderId,
          fromAddress: fromAddress!,
          fromAddressTitle: fromAddressTitle!,
          hasScales: viewHasScales,
          hasTrawl: viewHasTrawl,
          hasTrailer: viewHasTrailer,
        },
        signal,
      ),
    enabled: isAuthenticated && valid,
    staleTime: 30_000,
    retry: false,
  });

  // Предоплату диктует встречная сторона (покупатель) — продавец её не выбирает. Берём вариант из ссылки
  // (его выбрал расчёт лучшей цены); если такого варианта в заявке нет — первый доступный.
  const prepayment: SellPrepaymentType = useMemo(() => {
    const opts = view.data?.priceByOptions ?? {};
    if (opts[percentKeyFromPrepayment(initialPrepayment)]) {
      return initialPrepayment;
    }
    const firstKey = Object.keys(opts)[0];
    return firstKey ? prepaymentTypeFromPercent(firstKey) : initialPrepayment;
  }, [view.data, initialPrepayment]);

  const dealParams: SellDealParams = useMemo(() => {
    const base = {
      orderId,
      orderVersion,
      creationSection: 'SELL_BEST_PRICE' as const,
      isBestPrice: true,
      userComment: comment.trim(),
      prepaymentType: prepayment,
    };
    // С заявкой: бэкенд резолвит логистику/адрес (partnerAddress/loadingConditions слать нельзя).
    if (fromSaleOrderId != null) {
      return { ...base, fromSaleOrderId };
    }
    // Свободный режим: объём + логистика + адрес погрузки (мой базис) + декларация заполняет продавец.
    return {
      ...base,
      weightTon: logistics.weightTon === '' ? undefined : Number(logistics.weightTon),
      loadingConditions: buildLoadingConditions(logistics),
      partnerAddress: fromAddress ?? undefined,
      partnerAddressTitle: fromAddressTitle ?? undefined,
      declarationNo: logistics.declarationNo.trim() || undefined,
      declarationDate: isoToRu(logistics.declarationDate) ?? undefined,
      declarationFileIds: logistics.declarationFiles.map((file) => file.id),
    };
  }, [orderId, orderVersion, comment, fromSaleOrderId, prepayment, logistics, fromAddress, fromAddressTitle]);

  // Свободный режим: префилл объёма (один раз, если ещё не задан). Приоритет — объём, который продавец
  // уже ввёл в форме создания (prefillWeightTon из «Продать по ней»); иначе — объём заявки покупателя.
  useEffect(() => {
    if (!freeMode) {
      return;
    }
    const prefillRaw = params.get('prefillWeightTon');
    const prefill =
      prefillRaw != null && prefillRaw !== '' && Number.isFinite(Number(prefillRaw)) ? Number(prefillRaw) : undefined;
    const weight = prefill ?? view.data?.order?.weightTon ?? undefined;
    if (weight != null) {
      setLogistics((s) => (s.weightTon === '' ? { ...s, weightTon: weight } : s));
    }
  }, [freeMode, view.data, params]);

  // Свободный режим: смена объёма/логистики/предоплаты аннулирует сформированное поручение.
  const freeSignature = freeMode
    ? JSON.stringify({
        lc: buildLoadingConditions(logistics),
        w: logistics.weightTon,
        p: prepayment,
        dn: logistics.declarationNo.trim(),
        dd: logistics.declarationDate,
        df: logistics.declarationFiles.map((file) => file.id),
      })
    : '';
  const prevSignature = useRef(freeSignature);
  useEffect(() => {
    if (freeSignature !== prevSignature.current) {
      prevSignature.current = freeSignature;
      setAssignmentFileId(null);
      setAssignmentSigned(false);
    }
  }, [freeSignature]);

  const onMutationError = (error: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
    });
  };

  const generate = useMutation({
    mutationFn: () => orderService.generateBargainAgentAssignment(orderId, dealParams),
    onSuccess: (file) => {
      setAssignmentFileId(file.id);
      setAssignmentSigned(false);
      notifications.show({ color: 'lime', message: t('app.sell.deal.agent.generated') });
    },
    onError: onMutationError,
  });

  const sign = useMutation({
    mutationFn: () => orderService.signBargainAgentAssignment(assignmentFileId!),
    onSuccess: () => {
      setAssignmentSigned(true);
      notifications.show({ color: 'lime', message: t('app.sell.deal.agent.signed') });
    },
    onError: onMutationError,
  });

  const download = useMutation({
    mutationFn: () => orderService.downloadOrderFile(assignmentFileId!),
    onError: onMutationError,
  });

  // Декларация (только свободный режим): загрузка/удаление/скачивание — обязательна для сделки без заявки.
  const uploadDeclaration = useMutation({
    mutationFn: (files: File[]) => sellService.uploadDeclaration(files),
    onSuccess: (uploaded) => set({ declarationFiles: [...logistics.declarationFiles, ...uploaded] }),
    onError: onMutationError,
  });
  const removeDeclaration = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => set({ declarationFiles: logistics.declarationFiles.filter((file) => file.id !== fileId) }),
    onError: onMutationError,
  });
  const downloadDeclaration = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: onMutationError,
  });

  const sell = useMutation({
    mutationFn: async (): Promise<number> => {
      const processId = await orderService.createDealFromPurchase({ ...dealParams, agentAssignmentFileId: assignmentFileId! });
      const deadline = Date.now() + POLL_DEADLINE_MS;
      while (Date.now() < deadline && mountedRef.current) {
        await sleep(POLL_INTERVAL_MS);
        const status = await orderService.getBargainCreateStatus(processId);
        if (status.created && status.bargainId != null) {
          return status.bargainId;
        }
        if (status.suspended) {
          throw new DealSuspendedError();
        }
      }
      throw new DealTimeoutError();
    },
    onSuccess: (bargainId) => {
      notifications.show({ color: 'lime', message: t('app.buy.offer.dealCreated', { id: bargainId }) });
      navigate(`${ROUTES.APP_DEALS}/${bargainId}`);
    },
    onError: (error) => {
      if (!mountedRef.current) {
        return;
      }
      if (error instanceof DealTimeoutError) {
        notifications.show({ color: 'wheat', message: t('app.buy.offer.dealTimeout') });
        navigate(ROUTES.APP_DEALS);
        return;
      }
      if (error instanceof DealSuspendedError) {
        notifications.show({ color: 'red', message: t('app.buy.offer.dealSuspended') });
        return;
      }
      const status = getStatus(error);
      if (status === 410 || status === 424) {
        notifications.show({ color: 'red', message: t('app.sell.deal.gone') });
        return;
      }
      if (status === 412) {
        view.refetch();
        notifications.show({ color: 'wheat', message: t('app.buy.offer.changed') });
        return;
      }
      if (status === 422) {
        const envelope = error instanceof ApiError ? error.envelope : undefined;
        const fields = (envelope?.data as { requiredFields?: string[] } | undefined)?.requiredFields;
        notifications.show({
          color: 'red',
          message: fields?.length ? `${t('app.buy.offer.missingData')}: ${fields.join(', ')}` : t('app.buy.offer.missingData'),
        });
        return;
      }
      if (status === 423) {
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      onMutationError(error);
    },
  });

  // Агент не заключает сделок как сторона — он посредник. Заключение сделки продавцом (в т.ч. прямой
  // заход по URL) агенту недоступно: возвращаем к списку «Продать».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }

  if (!valid) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }

  // Назад — всегда к списку «Продать» (страница одного базиса убрана), а не к группе базиса.
  const backUrl = ROUTES.APP_SELL;

  const data = view.data;
  const order = data?.order;
  const viewStatus = getStatus(view.error);
  const offerGone = viewStatus === 410 || viewStatus === 424;

  const options = data?.priceByOptions ?? {};
  const priceInfo = options[percentKeyFromPrepayment(prepayment)];
  const fcaPrice = priceInfo?.fcaPricePerTonWithoutNds;
  // Итог продавцу. С заявкой — полная цена из расчёта по объёму заявки. В свободном режиме объём задаёт
  // продавец, поэтому считаем FCA × введённый тоннаж (до ввода — «—»).
  const totalFca = freeMode
    ? fcaPrice != null && logistics.weightTon !== ''
      ? fcaPrice * Number(logistics.weightTon)
      : undefined
    : priceInfo?.fullPriceFCAWithoutNds;
  const cptPrice = priceInfo?.cptPricePerTonWithoutNds;
  const acceptance = order?.qualityAcceptanceProperties ?? [];
  const creditKey = order?.qualityCreditType ? CREDIT_TYPE_KEYS[order.qualityCreditType] : undefined;
  // Сводка сделки: тоннаж — спрос покупателя (в свободном — введённый мной); качество — первое
  // свойство культуры (протеин).
  const summaryWeight = freeMode
    ? logistics.weightTon === ''
      ? undefined
      : Number(logistics.weightTon)
    : (data?.weightTon ?? order?.weightTon);
  const mainQuality = order?.goodsPropertiesSet?.properties?.[0];
  // В свободном режиме обязательны: логистика (объём + условия погрузки) И декларация (номер+дата+файлы).
  const declarationReady =
    logistics.declarationNo.trim() !== '' && !!logistics.declarationDate && logistics.declarationFiles.length > 0;
  const logisticsReady = !freeMode || (isDealLogisticsComplete(logistics) && declarationReady);
  const busy = generate.isPending || sign.isPending || sell.isPending;
  const canSell = assignmentSigned && agreeSupply && agreeOferta && !busy && !!priceInfo && logisticsReady;

  // Чего не хватает для сделки — единая красная сводка (как на формах создания). Цену/загрузку сюда не
  // включаем (это состояние данных, показывается отдельным Alert), только действия пользователя.
  const dealMissing = [
    freeMode && !isDealLogisticsComplete(logistics) ? 'app.sell.form.check.fields' : null,
    freeMode && !declarationReady ? 'app.sell.form.check.declaration' : null,
    !assignmentSigned ? 'app.sell.form.check.agent' : null,
    !agreeSupply || !agreeOferta ? 'app.sell.form.check.agree' : null,
  ].filter((key): key is string => key !== null);

  const prepaymentLabel = (type: SellPrepaymentType): string =>
    type === 'PERCENT_100' ? t('app.sell.prepayment100Short') : t('app.sell.prepayment0Short');

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          <Anchor component={Link} to={backUrl} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.deal.back')}
            </Group>
          </Anchor>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
              <IconWheat size={26} />
            </ThemeIcon>
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Title order={2}>{order?.goodsType ?? goods ?? t('app.sell.deal.title')}</Title>
              {order?.userInfo?.name ? (
                <Group gap={6} wrap="nowrap">
                  <IconBuilding size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                  <Text size="sm" c="gray.7">
                    {t('app.sell.deal.buyer')}:
                  </Text>
                  <Text fw={500}>{order.userInfo.name}</Text>
                  {order.userInfo.inn ? (
                    <Text size="sm" c="gray.7">
                      · {t('auth.fields.inn')} <IdText>{order.userInfo.inn}</IdText>
                    </Text>
                  ) : null}
                </Group>
              ) : null}
            </Stack>
          </Group>
        </Stack>

        {view.isError ? (
          <Alert color={offerGone ? 'wheat' : 'red'} variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>
                {offerGone
                  ? t('app.sell.deal.gone')
                  : isServiceUnavailable(view.error)
                    ? t('auth.errors.unavailable')
                    : t('auth.errors.generic')}
              </Text>
              {offerGone ? (
                <Button component={Link} to={backUrl} variant="subtle" size="compact-sm">
                  {t('app.sell.deal.back')}
                </Button>
              ) : (
                <Button variant="subtle" size="compact-sm" onClick={() => view.refetch()}>
                  {t('common.retry')}
                </Button>
              )}
            </Group>
          </Alert>
        ) : view.isLoading || !data ? (
          <Stack gap="sm">
            <Skeleton h={120} />
            <Skeleton h={220} />
          </Stack>
        ) : (
          <>
            {/* Сводка сделки (Вариант A): товар/качество/тоннаж → контраст «моя → лучшая (FCA)» + выигрыш → итоги. */}
            <DealSummaryCard
              goods={order?.goodsType ?? goods ?? t('app.sell.deal.title')}
              quality={mainQuality}
              weightTon={summaryWeight}
              action="sell"
              bestPrice={fcaPrice}
              bestPriceLabel={t('app.sell.deal.youGet')}
              myPrice={myPrice}
              advantage={advantage}
              prepaymentValue={prepayment}
              total={totalFca}
              totalLabel={t('app.sell.deal.total')}
              distance={data.distance}
              distanceLabel={t('app.sell.deal.distance')}
              extra={
                cptPrice != null ? (
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" c="gray.8">
                      {t('app.sell.deal.buyerPays')}:
                    </Text>
                    <Text fw={600}>{formatMoney(cptPrice)} ₽/т</Text>
                  </Group>
                ) : undefined
              }
            />

            {/* Маршрут: погрузка у меня → выгрузка у покупателя. */}
            <Card withBorder padding="lg">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                  <IconMapPin size={18} />
                </ThemeIcon>
                <Stack gap={6} style={{ minWidth: 0 }}>
                  <Text fw={600} size="sm">
                    {t('app.sell.deal.route')}
                  </Text>
                  <Group gap={8} wrap="wrap">
                    <Text size="sm" c="gray.8">
                      {formatOrgName(data.fromAddress?.title ?? fromAddressTitle)}
                    </Text>
                    <IconArrowRight size={14} color="var(--mantine-color-brand-6)" />
                    <Text size="sm" c="gray.8">
                      {formatOrgName(data.toAddress?.title ?? order?.addressTitle) ?? order?.address ?? '—'}
                    </Text>
                  </Group>
                </Stack>
              </Group>
            </Card>

            <Stack gap="sm">
              <SectionLabel>{t('app.sell.deal.termsTitle')}</SectionLabel>
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                      <IconClipboardCheck size={18} />
                    </ThemeIcon>
                    <Stack gap={6} style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm">
                        {t('app.buy.terms.acceptance')}
                      </Text>
                      <Text size="sm" c="gray.8">
                        {t('app.buy.terms.acceptanceGost')}
                      </Text>
                      {acceptance.length > 0 ? (
                        <Group gap={4}>
                          {acceptance.map((property) => (
                            <Badge key={property.name} variant="light" color="gray" tt="none" fw={500} size="lg" radius="sm">
                              {property.name}
                              {property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}
                            </Badge>
                          ))}
                        </Group>
                      ) : null}
                    </Stack>
                  </Group>

                  {creditKey ? (
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                        <IconScale size={18} />
                      </ThemeIcon>
                      <Stack gap={4} style={{ minWidth: 0 }}>
                        <Text fw={600} size="sm">
                          {t('app.buy.terms.credit')}
                        </Text>
                        <Text size="sm" c="gray.8">
                          {t(creditKey)}
                        </Text>
                      </Stack>
                    </Group>
                  ) : null}

                  {/* Предоплата задаётся покупателем (встречной стороной) — read-only, продавец не выбирает. */}
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                      <IconCoin size={18} />
                    </ThemeIcon>
                    <Stack gap={4} style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm">
                        {t('app.sell.deal.prepayment')}
                      </Text>
                      <Text size="sm" c="gray.8">
                        {prepaymentLabel(prepayment)}
                      </Text>
                    </Stack>
                  </Group>

                  {order?.userComment ? (
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                        <IconInfoCircle size={18} />
                      </ThemeIcon>
                      <Stack gap={4} style={{ minWidth: 0 }}>
                        <Text fw={600} size="sm">
                          {t('app.buy.terms.comment')}
                        </Text>
                        <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
                          {order.userComment}
                        </Text>
                      </Stack>
                    </Group>
                  ) : null}
                </Stack>
              </Card>
            </Stack>

            {/* Свободный режим (без заявки): продавец заполняет объём + условия погрузки. */}
            {freeMode ? (
              <Stack gap="sm">
                <SectionLabel>{t('app.sell.deal.myConditions')}</SectionLabel>
                <Card withBorder padding="lg">
                  <Stack gap="md">
                    <NumberInput
                      label={t('app.sell.deal.weight')}
                      value={logistics.weightTon}
                      onChange={(value) => set({ weightTon: value === '' ? '' : Number(value) })}
                      min={WEIGHT_MIN}
                      allowNegative={false}
                      clampBehavior="blur"
                      error={
                        logistics.weightTon !== '' && logistics.weightTon < WEIGHT_MIN
                          ? t('app.sell.deal.weightMin', { min: WEIGHT_MIN })
                          : undefined
                      }
                    />
                    <LoadingConditionsFields
                      state={logistics}
                      set={set}
                      shipmentModes={shipmentModes.data ?? []}
                      dateErrors={loadingDateErrors(logistics)}
                    />
                  </Stack>
                </Card>

                <SectionLabel>{t('app.sell.form.sectionDeclaration')}</SectionLabel>
                <Card withBorder padding="lg">
                  <Stack gap="md">
                    <Group grow align="flex-start" wrap="wrap">
                      <TextInput
                        label={t('app.sell.form.declaration.no')}
                        placeholder={t('app.sell.form.declaration.noPlaceholder')}
                        value={logistics.declarationNo}
                        maxLength={DECLARATION_NO_MAX}
                        onChange={(event) => set({ declarationNo: event.currentTarget.value })}
                      />
                      <TextInput
                        type="date"
                        label={t('app.sell.form.declaration.date')}
                        value={logistics.declarationDate}
                        max={todayIso()}
                        onChange={(event) => set({ declarationDate: event.currentTarget.value })}
                      />
                    </Group>
                    <FileList
                      files={logistics.declarationFiles}
                      onRemove={(id) => removeDeclaration.mutate(id)}
                      onDownload={(id) => downloadDeclaration.mutate(id)}
                      removing={removeDeclaration}
                    />
                    <UploadZone
                      accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.gif"
                      multiple
                      loading={uploadDeclaration.isPending}
                      hint={t('app.sell.form.declaration.filesHint')}
                      onFiles={(files) => uploadDeclaration.mutate(files)}
                    />
                  </Stack>
                </Card>
              </Stack>
            ) : data && (data.loadingConditions || data.declarationNo) ? (
              // Привязанный режим: мои условия read-only — погрузка/хранение/декларация (верхний уровень
              // ответа, бэкенд подтянул из моего оффера). Непересекающиеся с условиями покупателя;
              // особые условия/прочие — атрибут покупателя, показаны в его блоке.
              <Stack gap="sm">
                <SectionLabel>{t('app.sell.deal.myConditions')}</SectionLabel>
                <Card withBorder padding="lg">
                  <SellerCoreTermsView
                    order={data}
                    onDownload={(id) => downloadDeclaration.mutate(id)}
                    downloadingId={downloadDeclaration.isPending ? downloadDeclaration.variables : undefined}
                  />
                </Card>
              </Stack>
            ) : null}

            {/* Поручение агенту: сформировать → подписать. Без подписи продать нельзя. */}
            <Stack gap="sm">
              <SectionLabel>{t('app.sell.deal.agent.title')}</SectionLabel>
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Text size="sm" c="gray.8">
                    {t('app.sell.deal.agent.intro')}
                  </Text>
                  {assignmentFileId != null ? (
                    <Card withBorder padding="sm" bg="gray.0">
                      <Group justify="space-between" wrap="nowrap" gap="sm">
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <ThemeIcon variant="light" color={assignmentSigned ? 'lime' : 'wheat'} radius="md">
                            <IconFileText size={18} />
                          </ThemeIcon>
                          <Badge variant="light" color={assignmentSigned ? 'lime' : 'wheat'} tt="none" fw={500} radius="sm">
                            {assignmentSigned ? t('app.sell.deal.agent.signedBadge') : t('app.sell.deal.agent.readyBadge')}
                          </Badge>
                        </Group>
                        <ActionIcon
                          variant="default"
                          size={34}
                          aria-label={t('app.sell.deal.agent.download')}
                          loading={download.isPending}
                          onClick={() => download.mutate()}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ) : null}
                  <Group gap="sm" wrap="wrap">
                    <Button
                      variant="default"
                      leftSection={<IconFileText size={16} />}
                      loading={generate.isPending}
                      disabled={!priceInfo || sell.isPending || !logisticsReady}
                      onClick={() => generate.mutate()}
                    >
                      {assignmentFileId != null ? t('app.sell.deal.agent.regenerate') : t('app.sell.deal.agent.generate')}
                    </Button>
                    {assignmentFileId != null && !assignmentSigned ? (
                      <Button leftSection={<IconSignature size={16} />} loading={sign.isPending} onClick={() => sign.mutate()}>
                        {t('app.sell.deal.agent.sign')}
                      </Button>
                    ) : null}
                  </Group>
                </Stack>
              </Card>
            </Stack>

            <Textarea
              label={t('app.buy.offer.commentLabel')}
              placeholder={t('app.buy.offer.commentPlaceholder')}
              maxLength={4096}
              autosize
              minRows={2}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
            />

            <Stack gap="sm">
              <Checkbox
                checked={agreeSupply}
                onChange={(event) => setAgreeSupply(event.currentTarget.checked)}
                label={
                  <Text size="sm">
                    {t('app.buy.offer.agreeSupplyPrefix')}{' '}
                    <Anchor href={ROUTES.SUPPLY} target="_blank" size="sm" onClick={(event) => event.stopPropagation()}>
                      {t('app.buy.offer.agreeSupplyLink')}
                    </Anchor>
                    {t('app.buy.offer.agreeSupplySuffix')}
                  </Text>
                }
              />
              <Checkbox
                checked={agreeOferta}
                onChange={(event) => setAgreeOferta(event.currentTarget.checked)}
                label={<Text size="sm">{t('app.buy.offer.agreeOferta')}</Text>}
              />
            </Stack>

            {!priceInfo ? (
              <Alert color="wheat" variant="light" icon={<IconAlertTriangle size={18} />}>
                {t('app.sell.deal.noPrice')}
              </Alert>
            ) : null}

            <FormErrorsSummary title={t('common.fixFormErrors')} items={dealMissing.map((key) => t(key))} />

            <Group justify="flex-end" gap="sm">
              <Button component={Link} to={backUrl} variant="default" disabled={sell.isPending}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => sell.mutate()} loading={sell.isPending} disabled={!canSell}>
                {sell.isPending ? t('app.sell.deal.selling') : t('app.sell.deal.sell')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Container>
  );
}
